'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { geminiCache } from '@/lib/ai/geminiCache';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import type { ExpansionPriorityItem } from './ExpansionPriorityList';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ClusterGroup {
  name: string;
  cities: string[];
  rationale: string;
  growthPotential: 'High' | 'Medium' | 'Low';
}

interface MarketCorrelation {
  factor: string;
  markets: string[];
  insight: string;
}

interface ClusterAnalysisResult {
  clusters: ClusterGroup[];
  correlations: MarketCorrelation[];
  fastestGrowthCombo: {
    markets: string[];
    projectedGrowth: string;
    reasoning: string;
  };
  overallStrategy: string;
}

interface ClusterAnalysisPanelProps {
  markets: Array<{
    id: string;
    city: string;
    state: string;
    viability_score: number;
    population_growth: number;
    median_home_age: number;
    hail_events_5yr: number;
    annual_replacements_needed: number;
    is_active_market: boolean;
  }>;
  priorityItems: ExpansionPriorityItem[];
}

// ─── Growth badge config ──────────────────────────────────────────────────────
const GROWTH_CONFIG = {
  High:   { bg: '#ECFDF5', color: '#10B981', border: '#A7F3D0' },
  Medium: { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
  Low:    { bg: '#FEE2E2', color: '#EF4444', border: '#FECACA' },
};

// ─── Parse Gemini JSON response ───────────────────────────────────────────────
function parseAnalysis(raw: string): ClusterAnalysisResult | null {
  try {
    const jsonMatch = raw.match(/```json\s*([\s\S]*?)```/) || raw.match(/\{[\s\S]*\}/);
    const jsonStr = jsonMatch ? (jsonMatch[1] || jsonMatch[0]) : raw;
    return JSON.parse(jsonStr.trim());
  } catch {
    return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ClusterAnalysisPanel({ markets, priorityItems }: ClusterAnalysisPanelProps) {
  const [analysis, setAnalysis] = useState<ClusterAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRunMarketCount, setLastRunMarketCount] = useState(0);

  // Combine all markets: DB markets + priority list items
  const allMarkets = useCallback(() => {
    const combined: Array<{ city: string; state: string; viability_score?: number; population_growth?: number; median_home_age?: number; hail_events_5yr?: number; annual_replacements_needed?: number; stage?: string; is_active?: boolean }> = [];

    markets.forEach((m) => {
      combined.push({
        city: m.city,
        state: m.state,
        viability_score: m.viability_score,
        population_growth: m.population_growth,
        median_home_age: m.median_home_age,
        hail_events_5yr: m.hail_events_5yr,
        annual_replacements_needed: m.annual_replacements_needed,
        is_active: m.is_active_market,
      });
    });

    priorityItems.forEach((p) => {
      const alreadyIn = combined.some(
        (c) => c.city.toLowerCase() === p.city.toLowerCase() && c.state === p.state
      );
      if (!alreadyIn) {
        combined.push({ city: p.city, state: p.state, stage: p.stage });
      }
    });

    return combined;
  }, [markets, priorityItems]);

  const totalMarkets = allMarkets().length;

  const runAnalysis = useCallback(async () => {
    const combined = allMarkets();
    if (combined.length < 2) {
      setError('Add at least 2 markets to run cluster analysis.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);

    const marketList = combined
      .map((m, i) => {
        const parts = [`${i + 1}. ${m.city}, ${m.state}`];
        if (m.viability_score !== undefined) parts.push(`Viability: ${m.viability_score}/100`);
        if (m.population_growth !== undefined) parts.push(`Pop Growth: +${m.population_growth}%`);
        if (m.median_home_age !== undefined) parts.push(`Home Age: ${m.median_home_age}yr`);
        if (m.hail_events_5yr !== undefined) parts.push(`Hail Events: ${m.hail_events_5yr}`);
        if (m.annual_replacements_needed !== undefined) parts.push(`Annual Replacements: ${m.annual_replacements_needed.toLocaleString()}`);
        if (m.stage) parts.push(`Stage: ${m.stage}`);
        if (m.is_active) parts.push(`Status: Active Market`);
        return parts.join(' | ');
      })
      .join('\n');

    // Build a stable cache key from the sorted market list so re-ordering doesn't bust cache
    const cacheKey = `cluster_analysis_${combined
      .map((m) => `${m.city}_${m.state}`)
      .sort()
      .join('|')}`;

    try {
      const parsed = await geminiCache.get<ClusterAnalysisResult>(
        cacheKey,
        async () => {
          const result = await getChatCompletion(
            'GEMINI',
            'gemini/gemini-2.5-flash',
            [
              {
                role: 'system',
                content: `You are a strategic roofing market expansion analyst for NextRoof 360. Analyze a portfolio of expansion markets to identify geographic and demographic clusters, hidden correlations, and the optimal fastest-growth market combination. Return ONLY valid JSON with no markdown prose outside the JSON block.`,
              },
              {
                role: 'user',
                content: `Analyze these ${combined.length} expansion markets for NextRoof 360 (a roofing company):

${marketList}

Return a JSON object with this exact structure:
{
  "clusters": [
    {
      "name": "Cluster name (e.g., 'Southeast Storm Belt')",
      "cities": ["City, ST", ...],
      "rationale": "Why these markets cluster together (geography, demographics, storm patterns, etc.)",
      "growthPotential": "High" | "Medium" | "Low"
    }
  ],
  "correlations": [
    {
      "factor": "Correlation factor name (e.g., 'High Hail Frequency')",
      "markets": ["City, ST", ...],
      "insight": "What this correlation means for expansion strategy"
    }
  ],
  "fastestGrowthCombo": {
    "markets": ["City, ST", ...],
    "projectedGrowth": "e.g., '35-45% revenue uplift in 18 months'",
    "reasoning": "Why this specific combination maximizes growth speed"
  },
  "overallStrategy": "2-3 sentence strategic recommendation for the entire portfolio"
}

Identify 2-4 clusters, 2-3 correlations, and pick the top 2-4 markets for the fastest-growth combo. Be specific and data-driven.`,
              },
            ],
            { max_tokens: 1500, temperature: 0.4 }
          );

          const raw = result?.choices?.[0]?.message?.content || '';
          const p = parseAnalysis(raw);
          if (!p) throw new Error('parse_failed');
          return p;
        },
        { ttl: 2 * 60 * 60 * 1000 }
      );

      setAnalysis(parsed);
      setLastRunMarketCount(combined.length);
    } catch (err: any) {
      if (err?.message === 'parse_failed') {
        setError('Could not parse analysis. Please try again.');
      } else {
        setError('Analysis failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [allMarkets]);

  // Load cached analysis on mount (legacy sessionStorage key for backward compat)
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem('cluster_analysis');
      if (cached) {
        const { data, ts, count } = JSON.parse(cached);
        if (Date.now() - ts < 2 * 60 * 60 * 1000) {
          setAnalysis(data);
          setLastRunMarketCount(count);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const hasNewMarkets = totalMarkets !== lastRunMarketCount && lastRunMarketCount > 0;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: '#fff', border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: '#F3F4F6', background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 100%)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Icon name="SparklesIcon" size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-white">Gemini Cluster Analysis</h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Analyzes all {totalMarkets} saved markets together
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasNewMarkets && (
            <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#FEF3C7', color: '#D97706' }}>
              {totalMarkets - lastRunMarketCount > 0 ? `+${totalMarkets - lastRunMarketCount}` : ''} markets added — re-run
            </span>
          )}
          <button
            onClick={runAnalysis}
            disabled={loading || totalMarkets < 2}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: loading ? 'rgba(255,255,255,0.2)' : '#E89A3C', color: '#fff' }}
          >
            {loading ? (
              <>
                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Analyzing…
              </>
            ) : (
              <>
                <Icon name="BoltIcon" size={13} className="text-white" />
                {analysis ? 'Re-Analyze' : 'Run Analysis'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="p-6">
        {/* Empty / insufficient state */}
        {totalMarkets < 2 && !loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: '#F3F4F6' }}>
              <Icon name="ChartBarIcon" size={24} style={{ color: '#9CA3AF' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#374151' }}>Add at least 2 markets to run cluster analysis</p>
            <p className="text-xs text-center max-w-xs" style={{ color: '#9CA3AF' }}>
              Markets from the table above and the Expansion Priority List will all be analyzed together.
            </p>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: '#FEE2E2', border: '1px solid #FECACA' }}>
            <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#EF4444' }} />
            <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="h-4 rounded-full animate-pulse w-1/3" style={{ background: '#F3F4F6' }} />
              <div className="h-3 rounded-full animate-pulse w-full" style={{ background: '#F9FAFB' }} />
              <div className="h-3 rounded-full animate-pulse w-4/5" style={{ background: '#F9FAFB' }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="rounded-xl p-4 space-y-2" style={{ background: '#F9FAFB', border: '1px solid #F3F4F6' }}>
                  <div className="h-3 rounded-full animate-pulse w-2/3" style={{ background: '#F3F4F6' }} />
                  <div className="h-3 rounded-full animate-pulse w-full" style={{ background: '#F3F4F6' }} />
                  <div className="h-3 rounded-full animate-pulse w-3/4" style={{ background: '#F3F4F6' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {analysis && !loading && (
          <div className="space-y-7">

            {/* ── Overall Strategy ─────────────────────────────────────────── */}
            <div
              className="rounded-xl p-4"
              style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon name="LightBulbIcon" size={15} style={{ color: '#1C2E5E' }} />
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>Portfolio Strategy</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#1E3A5F' }}>{analysis.overallStrategy}</p>
            </div>

            {/* ── Fastest-Growth Combo ─────────────────────────────────────── */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#10B981' }}>
                  <Icon name="RocketLaunchIcon" size={12} className="text-white" />
                </div>
                <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Fastest-Growth Market Combination</h3>
              </div>
              <div
                className="rounded-xl p-5"
                style={{ background: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)', border: '1px solid #A7F3D0' }}
              >
                <div className="flex flex-wrap gap-2 mb-3">
                  {analysis.fastestGrowthCombo.markets.map((m) => (
                    <span
                      key={m}
                      className="text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{ background: '#10B981', color: '#fff' }}
                    >
                      {m}
                    </span>
                  ))}
                  <span
                    className="text-xs font-bold px-3 py-1.5 rounded-full ml-auto"
                    style={{ background: '#ECFDF5', color: '#059669', border: '1.5px solid #A7F3D0' }}
                  >
                    📈 {analysis.fastestGrowthCombo.projectedGrowth}
                  </span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: '#065F46' }}>{analysis.fastestGrowthCombo.reasoning}</p>
              </div>
            </div>

            {/* ── Clustering Opportunities ─────────────────────────────────── */}
            {analysis.clusters.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#E89A3C' }}>
                    <Icon name="MapPinIcon" size={12} className="text-white" />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Clustering Opportunities</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#EFF6FF', color: '#1C2E5E' }}>
                    {analysis.clusters.length} clusters identified
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysis.clusters.map((cluster, i) => {
                    const gc = GROWTH_CONFIG[cluster.growthPotential] || GROWTH_CONFIG.Medium;
                    return (
                      <div
                        key={i}
                        className="rounded-xl p-4"
                        style={{ background: '#FAFAFA', border: '1px solid #F3F4F6' }}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-sm font-bold" style={{ color: '#1C2E5E' }}>{cluster.name}</span>
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0"
                            style={{ background: gc.bg, color: gc.color, border: `1px solid ${gc.border}` }}
                          >
                            {cluster.growthPotential}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {cluster.cities.map((c) => (
                            <span
                              key={c}
                              className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ background: '#EFF6FF', color: '#1C2E5E' }}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{cluster.rationale}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Hidden Market Correlations ───────────────────────────────── */}
            {analysis.correlations.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#8B5CF6' }}>
                    <Icon name="ArrowsRightLeftIcon" size={12} className="text-white" />
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#1C2E5E' }}>Hidden Market Correlations</h3>
                </div>
                <div className="space-y-3">
                  {analysis.correlations.map((corr, i) => (
                    <div
                      key={i}
                      className="flex gap-4 rounded-xl p-4"
                      style={{ background: '#FAFAFA', border: '1px solid #F3F4F6' }}
                    >
                      <div className="shrink-0 mt-0.5">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black" style={{ background: '#F5F3FF', color: '#8B5CF6' }}>
                          {i + 1}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-xs font-bold" style={{ color: '#1C2E5E' }}>{corr.factor}</span>
                          <div className="flex flex-wrap gap-1">
                            {corr.markets.map((m) => (
                              <span
                                key={m}
                                className="text-xs px-1.5 py-0.5 rounded font-medium"
                                style={{ background: '#F5F3FF', color: '#7C3AED' }}
                              >
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{corr.insight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Footer timestamp ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
              <span className="text-xs" style={{ color: '#9CA3AF' }}>
                Analysis based on {lastRunMarketCount} markets · Powered by Gemini
              </span>
              <button
                onClick={runAnalysis}
                className="text-xs font-semibold flex items-center gap-1 hover:opacity-70 transition-opacity"
                style={{ color: '#E89A3C' }}
              >
                <Icon name="ArrowPathIcon" size={11} style={{ color: '#E89A3C' }} />
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* Prompt to run when markets exist but no analysis yet */}
        {!analysis && !loading && !error && totalMarkets >= 2 && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #DBEAFE' }}
            >
              <Icon name="SparklesIcon" size={24} style={{ color: '#E89A3C' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: '#374151' }}>
              {totalMarkets} markets ready for analysis
            </p>
            <p className="text-xs text-center max-w-xs" style={{ color: '#9CA3AF' }}>
              Click <strong>Run Analysis</strong> to identify clusters, correlations, and your fastest-growth market combination.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
